// Import base types from shared store types
import {
    TourData,
    FolderData,
    DeliveryNoteData,
} from '@app/shared/models/store-types';
import { DeliveryNoteWithOrgSiteKey } from '@app/stores/deliveryNotesStore';
import { EventWithOrgSiteKey } from '@app/stores/eventsStore';

// Import API types for expanded row data
import { DeliveryNote } from 'api/models/delivery-note';
import { DeliveryNoteInTour } from 'api/models/delivery-note-in-tour';
import { DeliveryNoteEvents } from 'api/models';

// Interface for delivery notes expanded with notification context
export interface NotificationDeliveryNote extends DeliveryNoteEvents {
    bundleNumber: string;
    bundleKey: string;
    parentEventKey: string;
    parentOrgSiteKey: string;
}

// Union type for all expanded row data types (used in expandable table views)
export type ExpandedRowData =
    | DeliveryNote // From bundle APIs (consignor/consignee/carrier folders)
    | DeliveryNoteInTour // From tour APIs (carrier/consignee tours)
    | NotificationDeliveryNote // From notification event processing
    | FolderData; // Bundles contained in a bundle group

// Union type for all supported table data types
export type TableRowData =
    | DeliveryNoteWithOrgSiteKey
    | DeliveryNoteData
    | FolderData
    | TourData
    | EventWithOrgSiteKey;

// Type guards for runtime type checking
export function isDeliveryNoteData(
    data: any
): data is DeliveryNoteWithOrgSiteKey | DeliveryNoteData {
    return data && typeof data === 'object' && 'number' in data;
}

export function isFolderData(data: any): data is FolderData {
    return data && typeof data === 'object' && 'bundleNumber' in data;
}

export function isTourData(data: any): data is TourData {
    return data && typeof data === 'object' && 'tourNumber' in data;
}

export function isEventData(data: any): data is EventWithOrgSiteKey {
    return (
        data &&
        typeof data === 'object' &&
        ('eventType' in data || 'eventKey' in data)
    );
}

// Type guards for expanded row data types
export function isDeliveryNote(data: any): data is DeliveryNote {
    return (
        data &&
        typeof data === 'object' &&
        'number' in data &&
        'fileName' in data &&
        'attachmentsCount' in data
    );
}

export function isDeliveryNoteInTour(data: any): data is DeliveryNoteInTour {
    return (
        data &&
        typeof data === 'object' &&
        'number' in data &&
        'fileName' in data &&
        'carrierName' in data &&
        'consigneeName' in data
    );
}

export function isNotificationDeliveryNote(
    data: any
): data is NotificationDeliveryNote {
    return (
        data &&
        typeof data === 'object' &&
        'bundleNumber' in data &&
        'bundleKey' in data &&
        'parentEventKey' in data
    );
}

export function isExpandedRowData(data: any): data is ExpandedRowData {
    return (
        isDeliveryNote(data) ||
        isDeliveryNoteInTour(data) ||
        isNotificationDeliveryNote(data)
    );
}

// Utility functions for safe property access
export function hasStatus(
    data: TableRowData
): data is TableRowData & { status: string } {
    return 'status' in data && typeof (data as any).status === 'string';
}

export function hasReadOnly(
    data: TableRowData
): data is TableRowData & { read_only: boolean } {
    return 'read_only' in data && typeof (data as any).read_only === 'boolean';
}

export function hasDeliveryNoteBundleStatus(
    data: TableRowData
): data is TableRowData & { deliveryNoteBundleStatus: string } {
    return (
        'deliveryNoteBundleStatus' in data &&
        typeof (data as any).deliveryNoteBundleStatus === 'string'
    );
}

export function hasNumberOfDeliveryNotes(
    data: TableRowData
): data is TableRowData & { numberOfDeliveryNotes: number } {
    return (
        'numberOfDeliveryNotes' in data &&
        typeof (data as any).numberOfDeliveryNotes === 'number'
    );
}

export function hasDateReceived(
    data: TableRowData
): data is TableRowData & { dateReceived: number } {
    return (
        'dateReceived' in data && typeof (data as any).dateReceived === 'number'
    );
}

export function hasFineDiscrepancies(
    data: TableRowData
): data is TableRowData & {
    fineDiscrepancies?: boolean;
    hasFineDiscrepancies?: boolean;
} {
    return (
        ('fineDiscrepancies' in data &&
            typeof (data as any).fineDiscrepancies === 'boolean') ||
        ('hasFineDiscrepancies' in data &&
            typeof (data as any).hasFineDiscrepancies === 'boolean')
    );
}

export function hasBundleKeys(data: TableRowData): data is TableRowData & {
    consignorKey: string;
    carrierKey: string;
    consigneeKey: string;
} {
    return (
        'consignorKey' in data &&
        'carrierKey' in data &&
        'consigneeKey' in data &&
        typeof (data as any).consignorKey === 'string' &&
        typeof (data as any).carrierKey === 'string' &&
        typeof (data as any).consigneeKey === 'string'
    );
}

export function hasKey(
    data: TableRowData
): data is TableRowData & { _key: string } {
    return '_key' in data && typeof (data as any)._key === 'string';
}

export function hasAlternateKey(
    data: TableRowData
): data is TableRowData & { key: string } {
    return 'key' in data && typeof (data as any).key === 'string';
}

export function hasDiscrepancies(data: TableRowData): data is TableRowData & {
    discrepancies?: boolean;
    hasDiscrepancies?: boolean;
} {
    return (
        ('discrepancies' in data &&
            typeof (data as any).discrepancies === 'boolean') ||
        ('hasDiscrepancies' in data &&
            typeof (data as any).hasDiscrepancies === 'boolean')
    );
}

export function hasPalletTickets(
    data: TableRowData
): data is TableRowData & { palletTickets: boolean } {
    return (
        'palletTickets' in data &&
        typeof (data as any).palletTickets === 'boolean'
    );
}

export function hasOrgSiteKey(
    data: TableRowData
): data is TableRowData & { orgSiteKey: string } {
    return 'orgSiteKey' in data && typeof (data as any).orgSiteKey === 'string';
}

export function hasDeliveryNoteBundleKey(
    data: TableRowData
): data is TableRowData & { deliveryNoteBundleKey: string[] } {
    return (
        'deliveryNoteBundleKey' in data &&
        Array.isArray((data as any).deliveryNoteBundleKey)
    );
}

export function hasDeliveryNoteBundleNumber(
    data: TableRowData
): data is TableRowData & { deliveryNoteBundleNumber: string } {
    return (
        'deliveryNoteBundleNumber' in data &&
        typeof (data as any).deliveryNoteBundleNumber === 'string'
    );
}

export function hasCarrierNumberPlateIncoming(
    data: TableRowData | undefined
): data is TableRowData & { carrierNumberPlateIncoming: string } {
    return (
        !!data &&
        'carrierNumberPlateIncoming' in data &&
        typeof (data as any).carrierNumberPlateIncoming === 'string'
    );
}

export function hasCarrierNumberPlateOutgoing(
    data: TableRowData | undefined
): data is TableRowData & { carrierNumberPlateOutgoing: string } {
    return (
        !!data &&
        'carrierNumberPlateOutgoing' in data &&
        typeof (data as any).carrierNumberPlateOutgoing === 'string'
    );
}

export function hasCheckoutDate(
    data: TableRowData
): data is TableRowData & { checkoutDate: number } {
    return (
        'checkoutDate' in data && typeof (data as any).checkoutDate === 'number'
    );
}

export function hasDateClosed(
    data: TableRowData
): data is TableRowData & { dateClosed: number } {
    return 'dateClosed' in data && typeof (data as any).dateClosed === 'number';
}

export function hasDateShipped(
    data: TableRowData
): data is TableRowData & { dateShipped: number } {
    return (
        'dateShipped' in data && typeof (data as any).dateShipped === 'number'
    );
}

/**
 * Triggers a browser file download from a Blob.
 */
export function downloadBlobAsFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('style', 'display:none;');
    document.body.appendChild(a);
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
}

// Re-export store types for convenience
export {
    DeliveryNoteWithOrgSiteKey,
    DeliveryNoteData,
    FolderData,
    TourData,
    EventWithOrgSiteKey,
    // Re-export API types for expanded row data
    DeliveryNote,
    DeliveryNoteInTour,
    DeliveryNoteEvents,
};
